import dayjs from 'dayjs';

const HIGHLIGHT_SERIALIZE_HEADER = 'type:textContent';
const LESSON_PATH_SEPARATOR = '/lessons/';
const TRUNCATED_SUFFIX = '....';
const NO_DATE_BUCKET_KEY = 'no-date';
const OBJECT_TEXT = '[object Object]';
const TYPE_RANK = { note: 0, highlight: 1, textNote: 2 };

/**
 * Rangy fragments of a highlight serialization, without the header.
 * Each fragment is "start$end$highlightId$className$containerId", where the container is the componentId.
 */
export const getSerializeFragments = (serialize = '') => {
  if (!serialize || serialize.indexOf(HIGHLIGHT_SERIALIZE_HEADER) !== 0) {
    return [];
  }
  return serialize.split('|').filter(part => part && part !== HIGHLIGHT_SERIALIZE_HEADER);
};

export const countHighlightFragments = (serialize = '') => getSerializeFragments(serialize).length;

const fragmentPart = (fragment, index) => fragment.split('$')[index] || '';

export const getLessonIdFromActivityId = (activityId = '') => {
  const separatorIndex = activityId.lastIndexOf(LESSON_PATH_SEPARATOR);
  if (separatorIndex === -1) {
    return activityId;
  }
  return activityId.substring(separatorIndex + LESSON_PATH_SEPARATOR.length);
};

export const findLessonInBook = (book, activityId) => {
  let lessonFound;
  let unitFound;

  if (!book || !book.content || !activityId) {
    return { lessonFound, unitFound };
  }

  book.content.some(unit => {
    return (unit.content || []).some(lesson => {
      const matchesChild = (lesson.children || []).some(child => child.activityId === activityId);
      const matchesLesson = lesson.activityId === activityId;

      if (matchesChild || matchesLesson) {
        unitFound = unit;
        lessonFound = lesson;
        return true;
      }

      return false;
    });
  });

  return { lessonFound, unitFound };
};

// A description cut at 40 chars can end in the middle of "[object Object]".
const stripTrailingObjectText = text => {
  for (let length = OBJECT_TEXT.length - 1; length >= 2; length--) {
    if (text.endsWith(OBJECT_TEXT.slice(0, length))) {
      return text.slice(0, -length);
    }
  }
  return text;
};

/**
 * Cleans a stored description for display:
 * - strips the backend's literal "...." truncation marker, so the UI renders its own lighter ellipsis;
 * - drops "[object Object]" left by the reader when it stringified rich text nodes;
 * - skips the UI ellipsis when the text already ends in one, to avoid "......".
 */
export const cleanTruncatedText = (text = '') => {
  if (!text) {
    return { text: '', truncated: false };
  }

  let cleaned = text;
  let truncated = false;
  if (cleaned.endsWith(TRUNCATED_SUFFIX)) {
    cleaned = stripTrailingObjectText(cleaned.slice(0, -TRUNCATED_SUFFIX.length));
    truncated = true;
  }

  cleaned = cleaned
    .split(OBJECT_TEXT)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) {
    return { text: '', truncated: false };
  }
  if (/(\.\.\.|…)$/.test(cleaned)) {
    truncated = false;
  }
  return { text: cleaned, truncated };
};

/**
 * Single seam for "when is this annotation from". Items created before backend started
 * stamping highlights/note-highlights have no timestamp and fall back to null.
 */
export const getAnnotationDate = (annotation = {}) => annotation.timestamp || null;

const expandNote = note => {
  const value = (note.value || '').trim();
  if (!value) {
    return [];
  }
  return [
    {
      ...note,
      type: 'note',
      value,
      pageId: getLessonIdFromActivityId(note.activityId),
      orderIndex: -1
    }
  ];
};

// Position of a component's fragment in the serialization. New records carry the fragment id
// (`highlightId`); older ones are matched by the fragment container, which used to be the componentId.
// Unmatched components go after the located ones, in array order.
const componentOrderIndex = (fragments, component, position) => {
  let index = -1;
  if (component.highlightId !== undefined && component.highlightId !== null) {
    index = fragments.findIndex(fragment => fragmentPart(fragment, 2) === String(component.highlightId));
  }
  if (index === -1) {
    index = fragments.findIndex(fragment => fragmentPart(fragment, 4) === component.componentId);
  }
  return index === -1 ? fragments.length + position : index;
};

const expandHighlight = highlight => {
  const fragments = getSerializeFragments(highlight.serialize);
  if (!fragments.length) {
    return [];
  }

  const components = highlight.components || [];
  // Legacy page-level records without components have no text to show: discarded (product decision).
  if (!components.length) {
    return [];
  }

  // One highlight can be stored split across several ranges of the same component: keep one entry per
  // componentId, preferring a copy that carries a timestamp, placed at its earliest range.
  const byComponentId = {};
  const orderByComponentId = {};
  const componentIds = [];
  components.forEach((component, position) => {
    if (!component || !component.componentId) {
      return;
    }
    const { componentId } = component;
    const orderIndex = componentOrderIndex(fragments, component, position);
    const current = byComponentId[componentId];
    if (!current) {
      byComponentId[componentId] = component;
      orderByComponentId[componentId] = orderIndex;
      componentIds.push(componentId);
      return;
    }
    orderByComponentId[componentId] = Math.min(orderByComponentId[componentId], orderIndex);
    if (!current.timestamp && component.timestamp) {
      byComponentId[componentId] = component;
    }
  });

  return componentIds
    .map(componentId => {
      const component = byComponentId[componentId];
      const { text, truncated } = cleanTruncatedText(component.description);
      return {
        ...highlight,
        type: 'highlight',
        componentId,
        description: text,
        truncated,
        timestamp: component.timestamp,
        orderIndex: orderByComponentId[componentId]
      };
    })
    .filter(entry => entry.description);
};

const expandNoteHighlight = noteHighlight => {
  const fragments = getSerializeFragments(noteHighlight.serialize);
  if (!fragments.length) {
    return [];
  }

  return (noteHighlight.notes || [])
    .filter(note => note && note.text && note.text.trim())
    .map((note, position) => {
      const index = fragments.findIndex(fragment => fragmentPart(fragment, 2) === String(note.highlightId));
      const { text: description, truncated } = cleanTruncatedText(note.description);
      return {
        type: 'textNote',
        bookActivityId: noteHighlight.bookActivityId,
        unitActivityId: noteHighlight.unitActivityId,
        pageId: noteHighlight.pageId,
        highlightId: note.highlightId,
        text: note.text.trim(),
        componentId: note.componentId,
        description,
        truncated,
        timestamp: note.timestamp,
        orderIndex: index === -1 ? fragments.length + position : index
      };
    });
};

/**
 * Flattens the three API lists into typed entries ('note' | 'highlight' | 'textNote'),
 * dropping empty records, empty notes and highlights without text.
 */
export const flattenAnnotations = (notes = [], highlights = [], noteHighlights = []) => [
  ...notes.filter(Boolean).flatMap(expandNote),
  ...highlights.filter(Boolean).flatMap(expandHighlight),
  ...noteHighlights.filter(Boolean).flatMap(expandNoteHighlight)
];

export const getPageCardKey = entry => `${entry.bookActivityId || ''}|${entry.unitActivityId || ''}|${entry.pageId || ''}`;

export const getLessonActivityId = entry =>
  entry.type === 'note' && entry.activityId ? entry.activityId : `${entry.unitActivityId}${LESSON_PATH_SEPARATOR}${entry.pageId}`;

// Page annotation first, then reading order. Highlights and text notes come from two different
// serializations, so their relative order is approximate when they sit in different components.
const compareCardEntries = (first, second) =>
  (first.type === 'note' ? 0 : 1) - (second.type === 'note' ? 0 : 1) ||
  first.orderIndex - second.orderIndex ||
  TYPE_RANK[first.type] - TYPE_RANK[second.type] ||
  (getAnnotationDate(first) || 0) - (getAnnotationDate(second) || 0);

/**
 * Groups entries into one card per page (book + unit + page). A card takes the date of its most
 * recent dated entry, so undated entries join their page's card; only pages with no dated entry at
 * all end up undated. Dated cards come newest first, undated ones last ordered by book › unit › page.
 */
export const buildPageCards = (entries = []) => {
  const cards = {};
  entries.forEach(entry => {
    const key = getPageCardKey(entry);
    if (!cards[key]) {
      cards[key] = {
        key,
        bookActivityId: entry.bookActivityId,
        unitActivityId: entry.unitActivityId,
        pageId: entry.pageId,
        lessonActivityId: getLessonActivityId(entry),
        date: null,
        entries: []
      };
    }
    const card = cards[key];
    card.entries.push(entry);
    const date = getAnnotationDate(entry);
    if (date && date > (card.date || 0)) {
      card.date = date;
    }
  });

  return Object.values(cards)
    .map(card => ({ ...card, entries: card.entries.sort(compareCardEntries) }))
    .sort((first, second) => {
      if (first.date === null && second.date === null) return first.key.localeCompare(second.key);
      if (first.date === null) return 1;
      if (second.date === null) return -1;
      return second.date - first.date;
    });
};

const dateBucketKey = date => (date ? dayjs(date).format('YYYY-MM-DD') : NO_DATE_BUCKET_KEY);

/**
 * Groups already sorted (and paginated) cards by day. Applied per page, so a day split across two
 * pages gets its header on both.
 */
export const groupCardsByDay = (cards = []) => {
  const groups = [];
  cards.forEach(card => {
    const key = dateBucketKey(card.date);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.cards.push(card);
    } else {
      groups.push({ key, date: card.date ? dayjs(card.date).startOf('day').valueOf() : null, cards: [card] });
    }
  });
  return groups.map(({ date, cards: dayCards }) => ({ date, cards: dayCards }));
};
